import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import {
  getLtcPool,
  getMonthPayout,
} from '@shared/calculations/funding/projection/projectionTotals';
import { carePhaseLabel, careEnvironmentLabel } from '../../glossary/enumLabels';
import { currency, monthDisplay } from './formatters';
import { milestoneBadgeClass } from './milestoneColors';
import type { StrategyPresentation } from './strategies/types';

const rangeLabel = (start: string | number, end: string | number) =>
  start === end ? String(start) : `${start}–${end}`;

function isHomeWaitingPeriodBucket(careEnvironment: CareEnvironment): boolean {
  return careEnvironment === CareEnvironment.home;
}

export function WhenTooltipContent({
  startMonth,
  endMonth,
}: {
  startMonth: MonthlyEntry;
  endMonth?: MonthlyEntry;
}) {
  const end = endMonth ?? startMonth;
  const ageText =
    startMonth.context.clientAge != null && end.context.clientAge != null
      ? rangeLabel(startMonth.context.clientAge, end.context.clientAge)
      : '—';
  const policyYearText = rangeLabel(startMonth.context.policyYear, end.context.policyYear);
  const startPhase = `${carePhaseLabel(startMonth.context.carePhase)} at ${careEnvironmentLabel(startMonth.context.careEnvironment)}`;
  const endPhase = `${carePhaseLabel(end.context.carePhase)} at ${careEnvironmentLabel(end.context.careEnvironment)}`;
  const phaseText = startPhase === endPhase ? startPhase : `${startPhase} → ${endPhase}`;

  return (
    <TooltipContent className="text-xs">
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Age</span>
        <span className="font-semibold">{ageText}</span>
        <span className="text-gray-300">Policy Year</span>
        <span className="font-semibold">{policyYearText}</span>
        <span className="text-gray-300">Care Phase</span>
        <span className="font-semibold">{phaseText}</span>
      </div>
    </TooltipContent>
  );
}

export function WhenCell({ month }: { month: MonthlyEntry }) {
  const milestones = month.events.milestones;

  return (
    <div className="flex flex-col gap-1.5 max-w-28">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block text-sm font-semibold leading-snug text-gray-900 cursor-help">
            Month {monthDisplay(month.context.monthIndex)}
          </span>
        </TooltipTrigger>
        <WhenTooltipContent startMonth={month} />
      </Tooltip>
      {milestones.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {milestones.map((m, mi) => (
            <span
              key={mi}
              className={`rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap ${milestoneBadgeClass(m.kind)}`}
            >
              {m.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function CareCell({ month }: { month: MonthlyEntry }) {
  return (
    <div className="text-xs text-gray-800">
      <span className="font-medium">{carePhaseLabel(month.context.carePhase)}</span>
      <span className="mx-1 text-gray-400">/</span>
      <span className="text-gray-600">{careEnvironmentLabel(month.context.careEnvironment)}</span>
    </div>
  );
}

export function CostCell({
  month,
  terms,
}: {
  month: MonthlyEntry;
  terms: ClaimTerms;
}) {
  const phaseStartYear =
    terms.phaseStartYears[month.context.carePhase] ?? terms.projectionAnchorYear;
  const yearsFromTodayContinuous =
    phaseStartYear - terms.projectionAnchorYear + month.context.monthsIntoPhase / 12;
  const yearsFromToday = Math.max(Math.floor(yearsFromTodayContinuous), 0);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">{currency(month.facts.effectiveCareCost)}</div>
          <div className="text-[10px] text-gray-500">
            {currency(month.facts.dailyCareCost)}/day
          </div>
        </div>
      </TooltipTrigger>
      <InflationTooltipContent
        startingLabel="Today's monthly cost"
        startingValue={month.facts.nominalCareCost}
        rate={terms.careCostInflationRate}
        years={yearsFromToday}
        result={month.facts.effectiveCareCost}
      />
    </Tooltip>
  );
}

export function BenefitAmountCell({
  snapshot,
  terms,
  presentation,
}: {
  snapshot: YearSnapshot;
  terms: ClaimTerms;
  presentation?: StrategyPresentation;
}) {
  const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
  const intYear = Math.floor(snapshot.policyYear);
  const cap = terms.inflationCapYears > 0 ? terms.inflationCapYears : null;
  const effectiveYear = cap !== null && intYear > cap ? cap : intYear;
  const increments = Math.max(effectiveYear - 1, 0);
  const capped = cap !== null && intYear > cap;
  const note = capped
    ? (presentation?.copy?.benefitInflationCapNote?.(cap) ?? `Inflation capped at year ${cap}`)
    : undefined;
  const isSimple = terms.inflationIsSimple === true;
  // Strategy "compound" label copy only applies when actually compounding.
  // Simple-form CM II falls back to a neutral label so a "Compound rider
  // growth" override doesn't mislabel linear growth.
  const growthLabel = isSimple
    ? 'Simple growth'
    : presentation?.copy?.benefitInflationCompoundLabel ?? 'Compound growth';
  const footer =
    presentation?.benefitAmountCellTooltipFooter?.({ snapshot, terms }) ?? null;
  // Headline = the per-month benefit ceiling this year. For CM II that's
  // baseMonthly + riderMonthly; for Asset Flex it's the ACIO-inflated
  // monthly cap. Both products store this on `monthlyBenefitCap`. The
  // tooltip formula uses the strategy's per-product rate (Asset Flex's
  // On-Face ACIO can differ from policy-wide ACIO) and the simple-vs-
  // compound mode flagged in `terms.inflationIsSimple`.
  const rate = presentation?.benefitInflationRate?.(terms) ?? terms.inflationRate;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">
            {currency(snapshot.monthlyBenefitCap)}
          </div>
          <div className="text-[10px] text-gray-500">
            {currency(snapshot.effectiveDailyBenefit)}/day
          </div>
        </div>
      </TooltipTrigger>
      <InflationTooltipContent
        header={`Policy year ${intYear}`}
        startingLabel="At issue"
        startingValue={baseMonthly}
        rate={rate}
        years={increments}
        result={snapshot.monthlyBenefitCap}
        note={note}
        compoundLabel={growthLabel}
        isSimple={isSimple}
        footer={footer}
      />
    </Tooltip>
  );
}

export function InflationTooltipContent({
  header,
  startingLabel,
  startingValue,
  rate,
  years,
  result,
  note,
  compoundLabel = 'Compound growth',
  isSimple = false,
  footer,
}: {
  header?: string;
  startingLabel: string;
  startingValue: number;
  rate: number;
  years: number;
  result: number;
  note?: string;
  compoundLabel?: string;
  isSimple?: boolean;
  footer?: ReactNode;
}) {
  const ratePct = (rate * 100).toFixed(2);
  return (
    <TooltipContent className="text-xs">
      {header && <div className="mb-1 font-semibold">{header}</div>}
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">{compoundLabel}</span>
        <span className="font-semibold tabular-nums">
          {isSimple ? (
            <>× (1 + {ratePct}% × {years})</>
          ) : (
            <>× (1 + {ratePct}%)<sup>{years}</sup></>
          )}
        </span>
        <span className="text-gray-300">Result</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        {note && (
          <>
            <span />
            <span className="text-[10px] text-amber-300">{note}</span>
          </>
        )}
      </div>
      {footer && <div className="mt-2 border-t border-gray-700 pt-1">{footer}</div>}
    </TooltipContent>
  );
}

export function EligibilityCell({
  month,
  openState,
}: {
  month: MonthlyEntry;
  openState: MonthlyState;
}) {
  const eligible = month.facts.coverageEligibleDays;
  const non2Adl = month.facts.non2AdlDays;
  const wpHome = month.facts.waitingPeriodDaysConsumedHome;
  const wpFacility = month.facts.waitingPeriodDaysConsumedFacility;
  const isHomeBucket = isHomeWaitingPeriodBucket(month.context.careEnvironment);
  const wpConsumed = isHomeBucket ? wpHome : wpFacility;
  const wpRemaining = isHomeBucket
    ? month.state.waitingPeriod.homeDays
    : month.state.waitingPeriod.facilityDays;
  const ineligible = Math.max(DAYS_IN_MONTH - eligible, 0);

  const reasons: { label: string; tone: string }[] = [];
  if (non2Adl > 0) {
    reasons.push({ label: `${non2Adl}d <2 ADL`, tone: 'bg-amber-50 text-amber-800' });
  }
  if (wpConsumed > 0) {
    reasons.push({ label: `${wpConsumed}d in WP`, tone: 'bg-rose-50 text-rose-800' });
  }

  const wpWasActive = isHomeBucket
    ? openState.waitingPeriod.homeDays > 0
    : openState.waitingPeriod.facilityDays > 0;

  return (
    <div className="leading-tight">
      <div>
        <span className="text-sm font-semibold text-gray-900">{eligible}</span>
        <span className="mx-1 text-gray-500">/</span>
        <span className="text-xs text-gray-600">{ineligible} ineligible</span>
      </div>
      {reasons.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {reasons.map((r, i) => (
            <span
              key={i}
              className={`rounded px-1 py-0 text-[10px] font-medium ${r.tone}`}
            >
              {r.label}
            </span>
          ))}
        </div>
      )}
      {wpRemaining > 0 && (
        <div className="mt-0.5 text-[10px] text-gray-600">WP remaining: {wpRemaining}d</div>
      )}
      {wpWasActive && wpRemaining === 0 && wpConsumed > 0 && (
        <div className="mt-0.5 text-[10px] font-medium text-emerald-700">WP satisfied</div>
      )}
    </div>
  );
}

export function PaidCell({
  month,
  cumulativeToDate,
  presentation,
  terms,
}: {
  month: MonthlyEntry;
  cumulativeToDate?: number;
  presentation?: StrategyPresentation;
  terms: ClaimTerms;
}) {
  const headline = getMonthPayout(month);
  const extras = presentation?.paidCellExtras?.(month) ?? null;
  const tooltipContent = presentation?.payoutCellTooltip?.({ month, terms }) ?? null;
  const ineligibilityReason = month.events.ineligibilityReason;
  const isHomeBucket = isHomeWaitingPeriodBucket(month.context.careEnvironment);
  const wpRemaining = isHomeBucket
    ? month.state.waitingPeriod.homeDays
    : month.state.waitingPeriod.facilityDays;
  const wpBucketLabel = isHomeBucket ? 'Home' : 'Facility';
  const zeroRowReason =
    ineligibilityReason === 'in-wp'
      ? `${wpBucketLabel} WP active${wpRemaining > 0 ? ` · ${wpRemaining}d remaining` : ''}`
      : ineligibilityReason === 'lt-2-adl'
        ? '<2 ADL active'
        : null;

  if (headline === 0 && extras == null && tooltipContent == null) {
    if (!zeroRowReason) return <span className="text-gray-300">—</span>;
    return (
      <div className="leading-tight">
        <div className="text-gray-300">—</div>
        <div className="text-[10px] text-amber-700">{zeroRowReason}</div>
      </div>
    );
  }

  const headlineNode = (
    <div className="font-medium text-gray-900">
      {currency(headline)}
      {cumulativeToDate !== undefined && (
        <span className="ml-1 font-normal text-gray-500">({currency(cumulativeToDate)})</span>
      )}
    </div>
  );

  return (
    <div className="leading-tight">
      {tooltipContent ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-help">{headlineNode}</div>
          </TooltipTrigger>
          {tooltipContent}
        </Tooltip>
      ) : (
        headlineNode
      )}
      {extras}
    </div>
  );
}

// Closing LTC pool + this-month delta. LTC pool is derived (base + eob)
// — read it via `getLtcPool`. Optional `tooltipContent` wraps in a Tooltip
// (used by strategies like Asset Flex to break out per-pool balances).
export function LtcPoolWithDeltaCell({
  openState,
  closeState,
  tooltipContent,
}: {
  openState: MonthlyState;
  closeState: MonthlyState;
  tooltipContent?: ReactNode;
}) {
  return (
    <BalanceWithDeltaCell
      opening={getLtcPool(openState)}
      closing={getLtcPool(closeState)}
      tooltipContent={tooltipContent}
    />
  );
}

// Closing balance + this-row delta (green = increase, rose = decrease).
// Closing is the headline (matches financial-statement convention and
// the month on which milestones like pool-exhausted fire); delta below
// reflects this month's change. Delta is omitted when zero. Optional
// `tooltipContent` wraps the cell in a Tooltip.
export function BalanceWithDeltaCell({
  opening,
  closing,
  tooltipContent,
}: {
  opening: number;
  closing: number;
  tooltipContent?: ReactNode;
}) {
  const delta = closing - opening;
  const inner = (
    <div className="leading-tight">
      <div className="font-medium text-gray-900">{currency(closing)}</div>
      {delta !== 0 && (
        <div className={`text-[10px] ${delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
          {delta > 0 ? '+' : '−'}
          {currency(Math.abs(delta))}
        </div>
      )}
    </div>
  );
  if (!tooltipContent) return inner;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{inner}</div>
      </TooltipTrigger>
      {tooltipContent}
    </Tooltip>
  );
}

// Increment count for a pool's inflation factor at a given policy year,
// matching the engine's `compoundFactor` (capped at `capYears`).
function poolIncrements(policyYear: number, capYears: number | undefined): number {
  const cap = capYears && capYears > 0 ? capYears : null;
  const intYear = Math.floor(policyYear);
  const effectiveYear = cap !== null && intYear > cap ? cap : intYear;
  return Math.max(effectiveYear - 1, 0);
}

// One pool's "at issue → grown → ceiling" derivation, as grid rows. Makes a
// flat ceiling self-explanatory: a 0% pool (e.g. Asset Care "AOB None") shows
// a "No inflation" line, and a pool past its cap shows a "cap reached" note —
// instead of silently dropping the growth row and looking broken.
function PoolDerivation({
  heading,
  atIssue,
  ceiling,
  rate,
  policyYear,
  capYears,
}: {
  heading?: string;
  atIssue: number;
  ceiling: number;
  rate: number;
  policyYear: number;
  capYears: number | undefined;
}) {
  const ratePct = (rate * 100).toFixed(2);
  const intYear = Math.floor(policyYear);
  const cap = capYears && capYears > 0 ? capYears : null;
  const capped = cap !== null && intYear > cap;
  const increments = poolIncrements(policyYear, capYears);
  // "Ceiling" only makes sense once inflation is capped or the pool is flat.
  // While inflation is still active the pool keeps growing every anniversary,
  // so the row value is just the year-N total, not a ceiling.
  const isTrueCeiling = rate <= 0 || capped;
  const resultLabel = isTrueCeiling
    ? rate <= 0
      ? 'Pool'
      : 'Ceiling'
    : `Year ${intYear} value`;
  return (
    <>
      {heading && (
        <div className="col-span-2 mt-1 text-[10px] uppercase tracking-wide text-gray-400">
          {heading}
        </div>
      )}
      <span className="text-gray-300">At issue</span>
      <span className="font-semibold tabular-nums">{currency(atIssue)}</span>
      {rate <= 0 ? (
        <span className="col-span-2 text-[10px] text-gray-400">No inflation — flat</span>
      ) : increments > 0 ? (
        <>
          <span className="text-gray-300">Growth ({ratePct}%/yr)</span>
          <span className="font-semibold tabular-nums">
            × (1 + {ratePct}%)<sup>{increments}</sup>
          </span>
          {capped ? (
            <span className="col-span-2 text-[10px] text-amber-300">
              Inflation cap reached (year {cap})
            </span>
          ) : (
            <span className="col-span-2 text-[10px] text-gray-400">
              Grows another {ratePct}% at each future anniversary{cap ? ` until year ${cap}` : ''}
            </span>
          )}
        </>
      ) : null}
      <span className="text-gray-300">{resultLabel}</span>
      <span className="font-semibold tabular-nums">{currency(ceiling)}</span>
    </>
  );
}

function PoolLimitTooltip({
  snapshot,
  terms,
}: {
  snapshot: YearSnapshot;
  terms: ClaimTerms;
}) {
  const intYear = Math.floor(snapshot.policyYear);
  const baseAtIssue = terms.baseBenefitPoolAtIssue;
  const eobAtIssue = terms.extensionOfBenefitsPoolAtIssue;
  const total = snapshot.theoreticalLtcPoolCeiling;
  const cobCeiling = snapshot.effectiveContinuationBenefit;
  const aobCeiling = total - cobCeiling;

  // No at-issue pool concept (CM II): the base pool is flat and does not
  // inflate — its growth lives in the monthly rider instead.
  if (baseAtIssue === undefined && eobAtIssue === undefined) {
    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Pool limit · policy year {intYear}</div>
        <div className="mb-1 text-[10px] text-gray-300">
          Flat base pool — it does not inflate. The inflation rider grows the
          monthly benefit instead (see Benefit amount).
        </div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Base pool</span>
          <span className="font-semibold tabular-nums">{currency(total)}</span>
        </div>
      </TooltipContent>
    );
  }

  const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const eobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const hasEob = (eobAtIssue ?? 0) > 0 || cobCeiling > 0;
  const baseCap = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const eobCap = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  // "Inflated ceiling" only applies once all inflating pools have hit their
  // caps (or have no inflation). Otherwise this is the year-N value — the
  // pool would keep growing on future anniversaries.
  const baseCapped = baseRate <= 0 || (baseCap > 0 && intYear > baseCap);
  const eobCapped = !hasEob || eobRate <= 0 || (eobCap > 0 && intYear > eobCap);
  const allCapped = baseCapped && eobCapped;
  const description = allCapped
    ? 'Inflated pool size if no benefits were used. Inflation has reached its cap, so this is the true ceiling.'
    : 'Inflated pool size at this policy year if no benefits were used. The pool keeps growing on each future anniversary until any inflation cap is reached.';
  const totalLabel = allCapped ? 'Total ceiling' : `Total · year ${intYear}`;

  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">Pool limit · policy year {intYear}</div>
      <div className="mb-1 text-[10px] text-gray-300">{description}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <PoolDerivation
          heading={hasEob ? 'Acceleration (AOB)' : undefined}
          atIssue={baseAtIssue ?? 0}
          ceiling={aobCeiling}
          rate={baseRate}
          policyYear={snapshot.policyYear}
          capYears={terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears}
        />
        {hasEob && (
          <PoolDerivation
            heading="Continuation (COB)"
            atIssue={eobAtIssue ?? 0}
            ceiling={cobCeiling}
            rate={eobRate}
            policyYear={snapshot.policyYear}
            capYears={terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears}
          />
        )}
        {hasEob && (
          <>
            <span className="mt-1 font-semibold text-gray-200">{totalLabel}</span>
            <span className="mt-1 font-semibold tabular-nums">{currency(total)}</span>
          </>
        )}
      </div>
    </TooltipContent>
  );
}

// Theoretical inflated pool ceiling for the row's policy year — what the
// pool(s) would be if no benefits were used. Spend-independent: reads the
// engine's `theoreticalLtcPoolCeiling` for the headline and the `terms`
// at-issue amounts for the derivation. Universal across products.
export function PoolLimitCell({
  snapshot,
  terms,
}: {
  snapshot: YearSnapshot | undefined;
  terms: ClaimTerms;
}) {
  if (!snapshot) return <span className="text-gray-300">—</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">
            {currency(snapshot.theoreticalLtcPoolCeiling)}
          </div>
        </div>
      </TooltipTrigger>
      <PoolLimitTooltip snapshot={snapshot} terms={terms} />
    </Tooltip>
  );
}

// Cumulative pool drawdown through this month, categorized: prior utilization
// (the AOB lump used before the projection) + Acceleration (AOB) + Continuation
// (COB) drawn. Total reconciles to the limit/remaining identity while a pool is
// open: Pool limit − Drawn = Pool remaining. Uses pool depletion (not cash
// paid), which can be less than payout for CM II (rider on top) / indemnity.
// Labels and copy for the Drawn tooltip body. Single-pool products (traditional
// LTC) pass `singlePool: true` and `baseLabel: 'Coverage pool drawn'` so the
// AOB/COB framing doesn't leak through; the cob row is hidden in that mode.
export interface DrawnCellLabels {
  description: string;
  baseLabel: string; // label for the `drawn.aob` row
  cobLabel?: string; // omit / hide when `singlePool`
  singlePool?: boolean;
}

const DEFAULT_DRAWN_LABELS: DrawnCellLabels = {
  description:
    'Cumulative reduction of the LTC pool. Pool limit − this = Pool remaining ' +
    '(while the pool is open). Reflects pool drawdown, which can be less than ' +
    'cash paid (indemnity / rider-on-top).',
  baseLabel: 'Acceleration (AOB) drawn',
  cobLabel: 'Continuation (COB) drawn',
};

export function CumulativeDrawnCell({
  drawn,
  priorUtilization,
  labels,
}: {
  drawn: { aob: number; cob: number } | undefined;
  priorUtilization: number;
  labels?: DrawnCellLabels;
}) {
  if (!drawn) return <span className="text-gray-300">—</span>;
  const total = priorUtilization + drawn.aob + drawn.cob;
  if (total === 0) return <span className="text-gray-300">—</span>;
  const L = labels ?? DEFAULT_DRAWN_LABELS;
  const showCob = !L.singlePool && drawn.cob > 0;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">{currency(total)}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Drawn down to date</div>
        <div className="mb-1 text-[10px] text-gray-300">{L.description}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {priorUtilization > 0 && (
            <>
              <span className="text-gray-300">Used before this projection</span>
              <span className="font-semibold tabular-nums">{currency(priorUtilization)}</span>
            </>
          )}
          {drawn.aob > 0 && (
            <>
              <span className="text-gray-300">{L.baseLabel}</span>
              <span className="font-semibold tabular-nums">{currency(drawn.aob)}</span>
            </>
          )}
          {showCob && L.cobLabel && (
            <>
              <span className="text-gray-300">{L.cobLabel}</span>
              <span className="font-semibold tabular-nums">{currency(drawn.cob)}</span>
            </>
          )}
          <span className="mt-1 font-semibold text-gray-200">Total drawn</span>
          <span className="mt-1 font-semibold tabular-nums">{currency(total)}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
